import { API } from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import {
  type BotPromptChannel,
  CHANNEL_AUTO_CRITERIA,
  KIND_NAMED_CRITERION_TYPES,
  KNOWN_STRUCTURED_KINDS,
  KNOWN_TOOL_NAMES,
  LOCATION_FIXTURE_ROLES,
  STORAGE_CATEGORIES,
  TOOL_NAMED_CRITERION_TYPES,
  USER_FIXTURE_ROLES,
  type UserStory,
  type UserStoryEvalSpec,
  type UserStoryStatus,
  deriveToolUsage
} from "@swivl-mono/types";
import { Button, ButtonColor, ButtonVariant, Input } from "@swivl-mono/ui";
import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useDomain } from "../../../../Models/Domain/Domain.Context";
import { Member } from "../../../../Models/Member/Member.Current";
import { readAttemptsPerCase } from "../../../AIStudio/Evals/AttemptsSetting";
import { readConcurrency } from "../../../AIStudio/Evals/ConcurrencySetting";
import { LastRunTranscript } from "./LastRunTranscript";
import { ObservedDependenciesPanel } from "./ObservedDependenciesPanel";
import { TurnVariablePicker, insertAtCaret } from "./TurnVariablePicker";

interface UserStoryDetailProps {
  story: UserStory;
  onSaved: (story: UserStory) => void;
  onDeleted: (id: string) => void;
  onClose: () => void;
}

const ALL_STATUSES: UserStoryStatus[] = ["backlog", "building", "live", "deprecated"];
const ALL_CHANNELS: BotPromptChannel[] = ["web", "sms", "voice"];

/**
 * Inline edit panel for a single user story. Top-level fields are directly
 * editable; the eval criteria sub-editor handles `turns[]` and `criteria[]`.
 *
 * Save calls `PUT /user-stories/:id` which writes to disk + regenerates the
 * markdown. The dirty-flag pattern mirrors the variables editor: track local
 * changes, enable Save when dirty, disable when clean.
 */
type DetailTab = "shared" | BotPromptChannel;

export function UserStoryDetail({ story, onSaved, onDeleted, onClose }: UserStoryDetailProps): JSX.Element {
  const [draft, setDraft] = useState<UserStory>(story);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<DetailTab>("shared");
  const [runState, setRunState] = useState<"idle" | "starting" | "started" | "error">("idle");
  const [startedRunId, setStartedRunId] = useState<number | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { studioPath } = useDomain();

  const isDirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(story), [draft, story]);

  const runnableChannels = useMemo(() => {
    const out: BotPromptChannel[] = [];
    for (const ch of ALL_CHANNELS) {
      const status = story.channelStatus[ch];
      if (!status || status === "deprecated") continue;
      if (story.evalByChannel?.[ch] === undefined && story.eval === undefined) continue;
      out.push(ch);
    }
    return out;
  }, [story]);

  const handleRunAllChannels = async (): Promise<void> => {
    if (!Member.orgId || runnableChannels.length === 0) return;
    setRunState("starting");
    setRunError(null);
    try {
      const { runId } = await API.BotEval.startRun({
        sourceOrgId: Member.orgId,
        suite: "user-stories",
        selectedCases: runnableChannels.map((channel) => ({ caseId: story.id, channel })),
        attemptsPerCase: readAttemptsPerCase(),
        concurrency: readConcurrency()
      });
      setStartedRunId(runId);
      setRunState("started");
    } catch (err) {
      setRunState("error");
      setRunError(err instanceof Error ? err.message : "Failed to start run");
    }
  };

  const update = <K extends keyof UserStory>(key: K, value: UserStory[K]): void => {
    setDraft({ ...draft, [key]: value });
  };

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      const result = await API.UserStories.upsert(draft);
      onSaved(result.story);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!confirm(`Delete story ${story.id}? This cannot be undone (well, except via git).`)) return;
    setSaving(true);
    setError(null);
    try {
      const result = await API.UserStories.remove(story.id);
      if (result.ok) onDeleted(story.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-b border-grey-200 bg-white px-4 py-3">
      <div className="grid grid-cols-2 gap-3">
        <Input label="Title" value={draft.title} onChange={(e) => update("title", e.target.value)} />
        <CategoryPicker value={draft.category} onChange={(v) => update("category", v)} />
      </div>

      <div className="mt-2">
        <label className="mb-1 block text-xs font-medium text-grey-600">Description</label>
        <textarea
          value={draft.description}
          onChange={(e) => update("description", e.target.value)}
          rows={3}
          className="w-full rounded border border-grey-200 px-2 py-1 text-sm"
        />
      </div>

      {(draft.createdBy || draft.createdAt) && (
        <div className="mt-2 text-xs text-grey-500">
          {draft.createdBy && (
            <>
              Created by <strong>{draft.createdBy}</strong>
            </>
          )}
          {draft.createdAt && <> on {draft.createdAt.slice(0, 10)}</>}
        </div>
      )}

      <ToolUsageChips story={draft} />

      <div className="mt-2">
        <label className="mb-1 block text-xs font-medium text-grey-600">Notes (markdown)</label>
        <textarea
          value={draft.notes ?? ""}
          onChange={(e) => update("notes", e.target.value || undefined)}
          rows={3}
          className="w-full rounded border border-grey-200 px-2 py-1 font-mono text-sm"
          placeholder="Free-form notes / design rationale / edge cases…"
        />
      </div>

      <DetailTabs draft={draft} activeTab={activeTab} setActiveTab={setActiveTab} update={update} storyId={story.id} />

      {error && <div className="mt-2 rounded border border-red-300 bg-red-50 px-2 py-1 text-xs text-red-700">{error}</div>}

      <div className="mt-3 flex items-center gap-2">
        <Button color={ButtonColor.BLUE} onClick={handleSave} disabled={!isDirty || saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
        <Button variant={ButtonVariant.GHOST} onClick={onClose} disabled={saving}>
          Close
        </Button>
        {runnableChannels.length > 0 && runState === "idle" && (
          <Button variant={ButtonVariant.GHOST} onClick={handleRunAllChannels} disabled={saving || isDirty}>
            ▶ Run all channels ({runnableChannels.join(", ")})
          </Button>
        )}
        {runState === "starting" && <span className="text-xs text-grey-500 italic">Starting run…</span>}
        {runState === "started" && startedRunId !== null && (
          <Button variant={ButtonVariant.GHOST} onClick={() => navigate(studioPath("testing", "evaluations", startedRunId))}>
            Open run #{startedRunId} →
          </Button>
        )}
        {runState === "error" && <span className="text-xs text-red-600">Run failed: {runError}</span>}
        <span className="ml-auto text-xs text-grey-400">Updated {draft.updatedAt}</span>
        <Button color={ButtonColor.LIGHT_GREY} variant={ButtonVariant.GHOST} onClick={handleDelete} disabled={saving}>
          Delete
        </Button>
      </div>
    </div>
  );
}

// ─── Category picker ───────────────────────────────────────────────────────

interface CategoryPickerProps {
  value: string;
  onChange: (value: string) => void;
}

// ─── Per-channel detail tabs ────────────────────────────────────────────────

interface DetailTabsProps {
  draft: UserStory;
  activeTab: DetailTab;
  setActiveTab: (t: DetailTab) => void;
  update: <K extends keyof UserStory>(key: K, value: UserStory[K]) => void;
  storyId: string;
}

/**
 * Tabbed view of per-channel state. The "Shared" tab edits the base eval used
 * as a fallback. Each channel tab edits that channel's status + per-channel
 * eval override (when toggled on).
 *
 * Plan: §P2.E.5 (Detail-view tab UX).
 */
function DetailTabs({ draft, activeTab, setActiveTab, update, storyId }: DetailTabsProps): JSX.Element {
  return (
    <div className="mt-3 rounded border border-grey-200">
      <div className="flex border-b border-grey-200 bg-grey-50">
        <TabButton label="Shared" active={activeTab === "shared"} onClick={() => setActiveTab("shared")} subline={evalSummary(draft.eval)} />
        {ALL_CHANNELS.map((c) => {
          const status = draft.channelStatus[c];
          const overridden = draft.evalByChannel?.[c] !== undefined;
          return (
            <TabButton
              key={c}
              label={c}
              active={activeTab === c}
              faded={!status}
              onClick={() => setActiveTab(c)}
              subline={status ? `${status}${overridden ? " · override" : ""}` : "n/a"}
            />
          );
        })}
      </div>
      <div className="p-3">
        {activeTab === "shared" ? (
          <SharedTab draft={draft} update={update} />
        ) : (
          <ChannelTab channel={activeTab} draft={draft} update={update} storyId={storyId} />
        )}
      </div>
    </div>
  );
}

interface TabButtonProps {
  label: string;
  subline: string;
  active: boolean;
  faded?: boolean;
  onClick: () => void;
}

function TabButton({ label, subline, active, faded, onClick }: TabButtonProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={filterClasses(
        "flex flex-col items-start border-r border-grey-200 px-4 py-2 text-left transition-colors",
        active ? "bg-white" : "hover:bg-grey-100",
        faded && !active && "opacity-50"
      )}
    >
      <span className={filterClasses("text-sm font-medium tracking-wide uppercase", active ? "text-primary-600" : "text-grey-700")}>{label}</span>
      <span className="text-[11px] text-grey-500">{subline}</span>
    </button>
  );
}

function evalSummary(spec?: UserStoryEvalSpec): string {
  if (!spec) return "no eval";
  return `${spec.criteria.length} criteria · ${spec.turns.length} turns`;
}

interface SharedTabProps {
  draft: UserStory;
  update: <K extends keyof UserStory>(key: K, value: UserStory[K]) => void;
}

function SharedTab({ draft, update }: SharedTabProps): JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-grey-500">
        Shared eval applies to every channel that doesn't define its own override (in the channel's tab). Edit channel-specific tweaks under the
        per-channel tabs.
      </p>
      {!draft.eval ? (
        <Button variant={ButtonVariant.GHOST} onClick={() => update("eval", { turns: [], criteria: [] })}>
          + Add shared eval spec
        </Button>
      ) : (
        <UserStoryEvalEditor spec={draft.eval} onChange={(spec) => update("eval", spec)} onRemove={() => update("eval", undefined)} />
      )}
      <ObservedDependenciesPanel observed={draft.observedDependencies} />
    </div>
  );
}

interface ChannelTabProps {
  channel: BotPromptChannel;
  draft: UserStory;
  update: <K extends keyof UserStory>(key: K, value: UserStory[K]) => void;
  storyId: string;
}

function ChannelTab({ channel, draft, update, storyId }: ChannelTabProps): JSX.Element {
  const status = draft.channelStatus[channel];
  const override = draft.evalByChannel?.[channel];
  const isOverriding = override !== undefined;
  const effectiveSpec = override ?? draft.eval;
  const navigate = useNavigate();
  const { studioPath } = useDomain();
  const [runState, setRunState] = useState<"idle" | "starting" | "started" | "error">("idle");
  const [startedRunId, setStartedRunId] = useState<number | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  const handleRun = async (): Promise<void> => {
    if (!Member.orgId) return;
    setRunState("starting");
    setRunError(null);
    try {
      const { runId } = await API.BotEval.startRun({
        sourceOrgId: Member.orgId,
        suite: "user-stories",
        selectedCases: [{ caseId: storyId, channel }],
        attemptsPerCase: readAttemptsPerCase(),
        concurrency: readConcurrency()
      });
      setStartedRunId(runId);
      setRunState("started");
    } catch (err) {
      setRunState("error");
      setRunError(err instanceof Error ? err.message : "Failed to start run");
    }
  };

  const setStatus = (next: UserStoryStatus | "__off__"): void => {
    const cs = { ...draft.channelStatus };
    if (next === "__off__") delete cs[channel];
    else cs[channel] = next;
    update("channelStatus", cs);
  };

  const setOverride = (next: UserStoryEvalSpec | undefined): void => {
    const ebc = { ...(draft.evalByChannel ?? {}) };
    if (next === undefined) delete ebc[channel];
    else ebc[channel] = next;
    update("evalByChannel", Object.keys(ebc).length > 0 ? ebc : undefined);
  };

  return (
    <div className="flex flex-col gap-3">
      <div>
        <label className="mb-1 block text-xs font-medium text-grey-600">Status on {channel}</label>
        <select
          value={status ?? "__off__"}
          onChange={(e) => setStatus(e.target.value as UserStoryStatus | "__off__")}
          className="w-full rounded border border-grey-200 px-2 py-1 text-sm"
        >
          <option value="__off__">— not applicable to {channel} —</option>
          {ALL_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {!status && <p className="text-xs text-grey-500">This story doesn&rsquo;t apply to the {channel} channel. Pick a status above to enable it.</p>}

      {status && effectiveSpec && (
        <div className="flex items-center gap-2 rounded border border-grey-200 bg-grey-50 px-2 py-1.5 text-xs">
          <span className="text-grey-600">Run this case on {channel}:</span>
          {runState === "idle" && (
            <button
              type="button"
              onClick={handleRun}
              className="border-primary-500 text-primary-600 hover:bg-primary-50 rounded border bg-white px-2 py-0.5 font-medium"
            >
              ▶ Run on {channel}
            </button>
          )}
          {runState === "starting" && <span className="text-grey-500 italic">Starting…</span>}
          {runState === "started" && startedRunId !== null && (
            <>
              <span className="text-teal-600">Run #{startedRunId} started.</span>
              <button
                type="button"
                onClick={() => navigate(studioPath("testing", "evaluations", startedRunId))}
                className="rounded border border-grey-300 bg-white px-2 py-0.5 text-grey-700 hover:bg-grey-100"
              >
                Open run →
              </button>
            </>
          )}
          {runState === "error" && <span className="text-red-600">Failed: {runError}</span>}
        </div>
      )}

      {status && !effectiveSpec && (
        <p className="rounded border border-yellow-300 bg-yellow-50 px-2 py-1 text-xs text-yellow-800">
          No eval defined yet. Add a shared eval (Shared tab) or override above to make this story runnable.
        </p>
      )}

      {status && (
        <div className="rounded border border-grey-200 p-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-grey-700">Eval for {channel}</label>
            <label className="flex items-center gap-1 text-xs text-grey-600">
              <input
                type="checkbox"
                checked={isOverriding}
                onChange={(e) => {
                  if (e.target.checked) {
                    // Seed the override from the shared eval (if any) so the user
                    // edits a copy, not a blank slate.
                    setOverride(
                      draft.eval ? { ...draft.eval, criteria: [...draft.eval.criteria], turns: [...draft.eval.turns] } : { turns: [], criteria: [] }
                    );
                  } else {
                    setOverride(undefined);
                  }
                }}
              />
              Override shared eval
            </label>
          </div>
          {!isOverriding && (
            <p className="mt-1 text-[11px] text-grey-500">
              Using the shared eval ({evalSummary(draft.eval)}). Toggle &ldquo;Override shared eval&rdquo; to customize for {channel} only.
            </p>
          )}
          {isOverriding && override && (
            <div className="mt-2">
              <UserStoryEvalEditor spec={override} onChange={setOverride} onRemove={() => setOverride(undefined)} />
            </div>
          )}
          {!isOverriding && effectiveSpec && (
            <details className="mt-2">
              <summary className="cursor-pointer text-xs text-grey-500">View shared eval (read-only)</summary>
              <div className="pointer-events-none mt-2 opacity-70">
                <UserStoryEvalEditor spec={effectiveSpec} onChange={() => undefined} onRemove={() => undefined} />
              </div>
            </details>
          )}
          {isOverriding && override && draft.eval && (
            <CriteriaProvenanceLegend overrideCriteria={override.criteria} sharedCriteria={draft.eval.criteria} channel={channel} />
          )}
        </div>
      )}

      {status && <ChannelDefaultsPanel channel={channel} />}

      {status && draft.lastEval?.[channel] && <LastRunTranscript storyId={storyId} channel={channel} lastEval={draft.lastEval[channel]} />}

      <ObservedDependenciesPanel observed={draft.observedDependencies} channel={channel} />
    </div>
  );
}

/**
 * Helper that surfaces which criteria in an `evalByChannel.<channel>` override
 * are inherited from the shared eval vs. authored only for this channel.
 * Read-only — purely informational; the actual editing happens in
 * `<UserStoryEvalEditor>`.
 */
function CriteriaProvenanceLegend({
  overrideCriteria,
  sharedCriteria,
  channel
}: {
  overrideCriteria: { check: string; weight: number }[];
  sharedCriteria: { check: string; weight: number }[];
  channel: BotPromptChannel;
}): JSX.Element | null {
  const sharedSet = new Set(sharedCriteria.map((c) => c.check.trim().toLowerCase()));
  const sharedHere = overrideCriteria.filter((c) => sharedSet.has(c.check.trim().toLowerCase()));
  const channelOnly = overrideCriteria.filter((c) => !sharedSet.has(c.check.trim().toLowerCase()));

  if (sharedHere.length === 0 && channelOnly.length === 0) return null;

  return (
    <div className="mt-2 rounded border border-grey-100 bg-grey-50 p-2 text-xs">
      <div className="mb-1 font-medium text-grey-700">Criteria provenance</div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="mb-0.5 text-[11px] text-grey-500">Shared ({sharedHere.length})</div>
          <ul className="list-disc pl-4 text-[11px] text-grey-500">
            {sharedHere.map((c, i) => (
              <li key={`s-${i}`}>{c.check.length > 80 ? `${c.check.slice(0, 80)}…` : c.check}</li>
            ))}
            {sharedHere.length === 0 && <li className="list-none italic">— none —</li>}
          </ul>
        </div>
        <div>
          <div className="mb-0.5 text-[11px] font-medium text-orange-600">
            {channel}-only ({channelOnly.length})
          </div>
          <ul className="list-disc pl-4 text-[11px] text-grey-700">
            {channelOnly.map((c, i) => (
              <li key={`c-${i}`}>{c.check.length > 80 ? `${c.check.slice(0, 80)}…` : c.check}</li>
            ))}
            {channelOnly.length === 0 && <li className="list-none italic">— none —</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}

/**
 * Channel defaults — read-only display of `CHANNEL_AUTO_CRITERIA` for the
 * active channel. These criteria are appended to every eval run on this
 * channel by the bot's CaseExpander, so authors should see them alongside
 * the per-story criteria to know the full set being judged.
 */
function ChannelDefaultsPanel({ channel }: { channel: BotPromptChannel }): JSX.Element {
  const defaults = CHANNEL_AUTO_CRITERIA[channel] ?? [];
  return (
    <div className="rounded border border-grey-200 bg-grey-50 p-2">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium text-grey-700">{channel} channel defaults</span>
        <span className="text-[10px] text-grey-500">auto-applied to every run · read-only</span>
      </div>
      {defaults.length === 0 ? (
        <p className="text-[11px] text-grey-500 italic">No channel-default criteria for {channel}.</p>
      ) : (
        <ul className="space-y-1 text-[11px] text-grey-700">
          {defaults.map((c, i) => (
            <li key={i} className="flex gap-2">
              <span className="shrink-0 rounded bg-grey-200 px-1 font-mono text-[10px] text-grey-700">w{c.weight}</span>
              <span>{c.check}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Category picker — combines a dropdown of canonical STORAGE_CATEGORIES with
 * a "+ New category" escape hatch that flips to a free-text input. Any category
 * not in the canonical list is shown as the current value but flagged "(custom)"
 * so users can see it isn't part of the curated set.
 */
function CategoryPicker({ value, onChange }: CategoryPickerProps): JSX.Element {
  const isCustom = value !== "" && !STORAGE_CATEGORIES.includes(value);
  const [editingFreeText, setEditingFreeText] = useState(isCustom);

  if (editingFreeText) {
    return (
      <div>
        <label className="mb-1 block text-xs font-medium text-grey-600">Category (custom)</label>
        <div className="flex gap-1">
          <input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="grow rounded border border-grey-200 px-2 py-1 text-sm"
            placeholder="New category name…"
          />
          <button
            type="button"
            onClick={() => {
              setEditingFreeText(false);
              if (!STORAGE_CATEGORIES.includes(value)) onChange(STORAGE_CATEGORIES[0] ?? "");
            }}
            className="rounded border border-grey-200 px-2 py-1 text-xs text-grey-600 hover:bg-grey-50"
            title="Cancel custom category"
          >
            ×
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-grey-600">Category</label>
      <select
        value={STORAGE_CATEGORIES.includes(value) ? value : "__new__"}
        onChange={(e) => {
          if (e.target.value === "__new__") {
            setEditingFreeText(true);
            onChange("");
          } else {
            onChange(e.target.value);
          }
        }}
        className="w-full rounded border border-grey-200 px-2 py-1 text-sm"
      >
        {STORAGE_CATEGORIES.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
        <option value="__new__">+ New category…</option>
      </select>
      {isCustom && <div className="mt-0.5 text-[10px] text-yellow-700">Currently set to a custom category not in the canonical list.</div>}
    </div>
  );
}

interface UserStoryEvalEditorProps {
  spec: UserStoryEvalSpec;
  onChange: (spec: UserStoryEvalSpec) => void;
  onRemove: () => void;
}

const ALL_CRITERION_TYPES = [
  "response",
  "tool_called",
  "tool_not_called",
  "tool_input",
  "tool_output",
  "structured_message_emitted",
  "buttons_in_menu"
] as const;

/**
 * Dropdown for picking a canonical fixture role. Empty value = use the
 * harness default (`standard` for locations, `prospect` for users).
 * Falls back to "(custom: <value>)" if a story carries a role that's no
 * longer in the canonical list — surfaces the drift without losing data.
 */
function FixturePicker({
  label,
  value,
  options,
  defaultLabel,
  onChange
}: {
  label: string;
  value: string | undefined;
  options: readonly string[];
  defaultLabel: string;
  onChange: (value: string | undefined) => void;
}): JSX.Element {
  const isCustom = value !== undefined && value !== "" && !options.includes(value);
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-grey-600">{label}</label>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || undefined)}
        className="w-full rounded border border-grey-200 px-2 py-1 text-sm"
      >
        <option value="">{defaultLabel}</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
        {isCustom && (
          <option key={value} value={value}>
            {value} (custom — not in canonical list)
          </option>
        )}
      </select>
    </div>
  );
}

function UserStoryEvalEditor({ spec, onChange, onRemove }: UserStoryEvalEditorProps): JSX.Element {
  const updateField = <K extends keyof UserStoryEvalSpec>(key: K, value: UserStoryEvalSpec[K]): void => {
    onChange({ ...spec, [key]: value });
  };

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <FixturePicker
          label="Location fixture"
          value={spec.locationFixture}
          options={LOCATION_FIXTURE_ROLES}
          defaultLabel="standard (default)"
          onChange={(v) => updateField("locationFixture", v)}
        />
        <FixturePicker
          label="User fixture"
          value={spec.userFixture}
          options={USER_FIXTURE_ROLES}
          defaultLabel="prospect (default)"
          onChange={(v) => updateField("userFixture", v)}
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-grey-600">Judge context</label>
        <textarea
          value={spec.judgeContext ?? ""}
          onChange={(e) => updateField("judgeContext", e.target.value || undefined)}
          rows={2}
          className="w-full rounded border border-grey-200 px-2 py-1 text-sm"
        />
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <label className="text-xs font-medium text-grey-600">Turns</label>
          <Button
            variant={ButtonVariant.GHOST}
            onClick={() => updateField("turns", [...spec.turns, { role: "user", content: "" }])}
            className="text-xs"
          >
            + Turn
          </Button>
        </div>
        {spec.turns.map((turn, i) => (
          <TurnEditor
            key={i}
            turn={turn}
            onChange={(next) => {
              const all = [...spec.turns];
              all[i] = next;
              updateField("turns", all);
            }}
            onRemove={() =>
              updateField(
                "turns",
                spec.turns.filter((_, idx) => idx !== i)
              )
            }
          />
        ))}
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <label className="text-xs font-medium text-grey-600">Criteria</label>
          <Button
            variant={ButtonVariant.GHOST}
            onClick={() => updateField("criteria", [...spec.criteria, { check: "", weight: 2 }])}
            className="text-xs"
          >
            + Criterion
          </Button>
        </div>
        {spec.criteria.map((c, i) => (
          <div key={i} className="mb-1 grid grid-cols-[1fr_auto_auto_auto_auto_auto] gap-1 text-xs">
            <CriterionCheckInput
              value={c.check}
              onChange={(value) => {
                const next = [...spec.criteria];
                next[i] = { ...next[i], check: value };
                updateField("criteria", next);
              }}
            />
            <select
              value={c.weight}
              onChange={(e) => {
                const next = [...spec.criteria];
                next[i] = { ...next[i], weight: Number(e.target.value) as 1 | 2 | 3 };
                updateField("criteria", next);
              }}
              className="rounded border border-grey-200 px-1"
            >
              <option value={1}>w1</option>
              <option value={2}>w2</option>
              <option value={3}>w3</option>
            </select>
            <select
              value={c.type ?? "response"}
              onChange={(e) => {
                const next = [...spec.criteria];
                const v = e.target.value;
                const newType = v === "response" ? undefined : (v as (typeof ALL_CRITERION_TYPES)[number]);
                // Reset fields that no longer apply when the type changes
                // so stale toolName/kind don't sit on the criterion.
                const isToolNamed = (TOOL_NAMED_CRITERION_TYPES as readonly string[]).includes(v);
                const isKindNamed = (KIND_NAMED_CRITERION_TYPES as readonly string[]).includes(v);
                next[i] = {
                  ...next[i],
                  type: newType,
                  toolName: isToolNamed ? next[i].toolName : undefined,
                  kind: isKindNamed ? next[i].kind : undefined
                };
                updateField("criteria", next);
              }}
              className="rounded border border-grey-200 px-1"
            >
              {ALL_CRITERION_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            {(TOOL_NAMED_CRITERION_TYPES as readonly string[]).includes(c.type ?? "response") && (
              <select
                value={c.toolName ?? ""}
                onChange={(e) => {
                  const next = [...spec.criteria];
                  next[i] = { ...next[i], toolName: e.target.value || undefined };
                  updateField("criteria", next);
                }}
                className="rounded border border-grey-200 px-1"
              >
                <option value="">— pick a tool —</option>
                {KNOWN_TOOL_NAMES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
                {c.toolName && !KNOWN_TOOL_NAMES.includes(c.toolName as (typeof KNOWN_TOOL_NAMES)[number]) && (
                  <option value={c.toolName}>{c.toolName} (unknown)</option>
                )}
              </select>
            )}
            {(KIND_NAMED_CRITERION_TYPES as readonly string[]).includes(c.type ?? "response") && (
              <select
                value={c.kind ?? ""}
                onChange={(e) => {
                  const next = [...spec.criteria];
                  next[i] = { ...next[i], kind: e.target.value || undefined };
                  updateField("criteria", next);
                }}
                className="rounded border border-grey-200 px-1"
              >
                <option value="">— pick a kind —</option>
                {KNOWN_STRUCTURED_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
                {c.kind && !KNOWN_STRUCTURED_KINDS.includes(c.kind as (typeof KNOWN_STRUCTURED_KINDS)[number]) && (
                  <option value={c.kind}>{c.kind} (unknown)</option>
                )}
              </select>
            )}
            <button
              type="button"
              onClick={() =>
                updateField(
                  "criteria",
                  spec.criteria.filter((_, idx) => idx !== i)
                )
              }
              className="text-red-600"
            >
              ×
            </button>
          </div>
        ))}
      </div>

      <Button variant={ButtonVariant.GHOST} onClick={onRemove} className="text-xs">
        Remove eval spec
      </Button>
    </div>
  );
}

// ─── Turn + criterion sub-editors with template-variable picker ──────────────

interface TurnEditorProps {
  turn: { role: "user" | "expect_greeting"; content?: string };
  onChange: (next: { role: "user" | "expect_greeting"; content?: string }) => void;
  onRemove: () => void;
}

function TurnEditor({ turn, onChange, onRemove }: TurnEditorProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="mb-1 flex items-start gap-1">
      <select
        value={turn.role}
        onChange={(e) => onChange({ ...turn, role: e.target.value as "user" | "expect_greeting" })}
        className="rounded border border-grey-200 px-1 text-xs"
      >
        <option value="user">user</option>
        <option value="expect_greeting">expect_greeting</option>
      </select>
      <input
        ref={inputRef}
        value={turn.content ?? ""}
        onChange={(e) => onChange({ ...turn, content: e.target.value })}
        className="grow rounded border border-grey-200 px-2 py-1 text-xs"
        placeholder={turn.role === "user" ? "User message…" : "(no content for expect_greeting)"}
      />
      <TurnVariablePicker
        triggerLabel="+ var"
        onInsert={(token) => insertAtCaret(inputRef.current, token, turn.content ?? "", (next) => onChange({ ...turn, content: next }))}
      />
      <button type="button" onClick={onRemove} className="text-xs text-red-600">
        ×
      </button>
    </div>
  );
}

interface CriterionCheckInputProps {
  value: string;
  onChange: (value: string) => void;
}

function CriterionCheckInput({ value, onChange }: CriterionCheckInputProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="flex items-center gap-1">
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="grow rounded border border-grey-200 px-2 py-1"
        placeholder="Check description…"
      />
      <TurnVariablePicker triggerLabel="+ var" onInsert={(token) => insertAtCaret(inputRef.current, token, value, onChange)} />
    </div>
  );
}

/**
 * Read-only summary of which bot tools this story exercises. Derived on the
 * fly via `deriveToolUsage` — walks shared `eval.criteria` plus every
 * `evalByChannel[c].criteria`, picks up explicit `toolName` fields and prose
 * mentions. Hidden when the story has no eval (scoping/design entries).
 */
function ToolUsageChips({ story }: { story: UserStory }): JSX.Element | null {
  const tools = useMemo(() => [...deriveToolUsage(story)].sort(), [story]);
  if (tools.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1 text-xs">
      <span className="text-grey-500">Tools used:</span>
      {tools.map((tool) => (
        <span key={tool} className="rounded bg-purple-50 px-1.5 py-0.5 font-mono text-purple-700" title="Derived from this story's criteria">
          {tool}
        </span>
      ))}
    </div>
  );
}
